import { Component, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import { SnackBarService } from '../../../../services/snack-bar.service';

type AcceptanceStatus = 'accepted' | 'not accepted';

@Component({
  selector: 'app-commercial-visibility-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
  ],
  templateUrl: './commercial-visibility-dialog.component.html',
  styleUrl: './commercial-visibility-dialog.component.scss',
})
export class CommercialVisibilityDialogComponent implements OnInit {
  agreed = false;
  isSubmitting = false;
  acceptanceStatus: AcceptanceStatus = 'not accepted';
  acceptedAt: string | null = null;
  private currentTrackingId: number | string | null = null;

  get acceptedStatusLabel(): string {
    return this.acceptedAt
      ? `Accettata il ${this.acceptedAt}`
      : 'Consenso accettato';
  }

  get canRevoke(): boolean {
    return this.acceptanceStatus === 'accepted' && !!this.currentTrackingId;
  }

  constructor(
    private dialogRef: MatDialogRef<CommercialVisibilityDialogComponent>,
    private authService: AuthService,
    private snackBarService: SnackBarService,
  ) {}

  ngOnInit(): void {
    const codImpresa = sessionStorage.getItem('impresaCodiceFiscale');

    if (!codImpresa) {
      return;
    }

    this.authService
      .getConditionsTrackingStatus(codImpresa, 'visibilitaCommerciale')
      .subscribe((response) => {
        this.currentTrackingId = this.extractTrackingId(response);
        this.acceptedAt = this.extractAcceptedAt(response);
        const accepted = this.hasTrackingValue(response);
        this.acceptanceStatus = accepted ? 'accepted' : 'not accepted';
        this.agreed = accepted;
      });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  revoke(): void {
    const roleId = Number(sessionStorage.getItem('profiloId'));

    if (!this.currentTrackingId) {
      this.acceptanceStatus = 'not accepted';
      this.agreed = false;
      this.acceptedAt = null;
      return;
    }

    if (Number.isNaN(roleId)) {
      return;
    }

    this.isSubmitting = true;
    this.authService
      .patchConditionsTracking(this.currentTrackingId, {
        deleted: true,
        roleId,
        genericData: this.buildGenericData(),
      })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.acceptanceStatus = 'not accepted';
          this.agreed = false;
          this.acceptedAt = null;
          this.currentTrackingId = null;
          this.snackBarService.openSnackBar(
            'Revoca effettuata con successo',
            'openSnackBar.close',
          );
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackBarService.errorSnackBar(
            'Errore durante la revoca della visibilita commerciale',
            'openSnackBar.close',
          );
        },
      });
  }

  confirm(): void {
    const userId = Number(sessionStorage.getItem('id'));
    const roleId = Number(sessionStorage.getItem('profiloId'));

    if (this.acceptanceStatus === 'accepted') {
      this.snackBarService.openSnackBar(
        "Visibilita' gia' accetata",
        'openSnackBar.close',
      );
      return;
    }

    if (!this.agreed || Number.isNaN(userId) || Number.isNaN(roleId)) {
      return;
    }

    this.isSubmitting = true;

    this.authService
      .saveConditionsTracking({
        userId,
        roleId,
        conditionDescription: 'visibilitaCommerciale',
        genericData: this.buildGenericData(),
      })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.acceptanceStatus = 'accepted';
          this.agreed = true;
          this.acceptedAt = this.formatAcceptedAt(new Date().toISOString());
          this.snackBarService.openSnackBar(
            'Visibilita commerciale accettata con successo',
            'openSnackBar.close',
          );
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackBarService.errorSnackBar(
            'Errore durante il salvataggio della visibilita commerciale',
            'openSnackBar.close',
          );
        },
      });
  }

  private hasTrackingValue(response: unknown): boolean {
    if (response === null || response === undefined) {
      return false;
    }

    if (Array.isArray(response)) {
      const firstItem = response[0] as Record<string, unknown> | undefined;
      return this.isValidTrackingRecord(firstItem);
    }

    if (typeof response === 'string') {
      return response.trim().length > 0;
    }

    if (typeof response === 'object') {
      return this.isValidTrackingRecord(response as Record<string, unknown>);
    }

    return true;
  }

  private extractTrackingId(response: unknown): number | string | null {
    if (!response) {
      return null;
    }

    if (Array.isArray(response)) {
      const firstItem = response[0] as Record<string, unknown> | undefined;
      return this.extractIdFromObject(firstItem);
    }

    if (typeof response === 'object') {
      return this.extractIdFromObject(response as Record<string, unknown>);
    }

    return null;
  }

  private extractIdFromObject(
    value: Record<string, unknown> | undefined,
  ): number | string | null {
    if (!value) {
      return null;
    }

    const candidate = value['id'];
    return typeof candidate === 'number' || typeof candidate === 'string'
      ? candidate
      : null;
  }

  private isValidTrackingRecord(
    value: Record<string, unknown> | undefined,
  ): boolean {
    if (!value) {
      return false;
    }

    const candidateId = value['id'];
    if (candidateId === 0 || candidateId === '0') {
      return false;
    }

    return Object.keys(value).length > 0;
  }

  private extractAcceptedAt(response: unknown): string | null {
    if (!response) {
      return null;
    }

    if (Array.isArray(response)) {
      return this.extractAcceptedAtFromObject(
        response[0] as Record<string, unknown> | undefined,
      );
    }

    if (typeof response === 'object') {
      return this.extractAcceptedAtFromObject(response as Record<string, unknown>);
    }

    return null;
  }

  private extractAcceptedAtFromObject(
    value: Record<string, unknown> | undefined,
  ): string | null {
    if (!value) {
      return null;
    }

    const candidate = value['date'];
    if (typeof candidate === 'string' || candidate instanceof Date) {
      return this.formatAcceptedAt(candidate);
    }

    return null;
  }

  private formatAcceptedAt(value: string | Date): string | null {
    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return formatDate(date, "dd/MM/yyyy 'alle' HH:mm", 'it-IT');
  }

  private buildGenericData(): Record<string, unknown> {
    const nav = navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
        saveData?: boolean;
      };
      deviceMemory?: number;
      userAgentData?: {
        mobile?: boolean;
        platform?: string;
        brands?: Array<{ brand: string; version: string }>;
      };
    };

    return {
      appVersion: navigator.appVersion,
      colorDepth: window.screen.colorDepth,
      cookieEnabled: navigator.cookieEnabled,
      deviceMemory: nav.deviceMemory ?? null,
      devicePixelRatio: window.devicePixelRatio,
      hardwareConcurrency: navigator.hardwareConcurrency ?? null,
      innerHeight: window.innerHeight,
      innerWidth: window.innerWidth,
      language: navigator.language,
      languages: navigator.languages,
      maxTouchPoints: navigator.maxTouchPoints ?? 0,
      online: navigator.onLine,
      outerHeight: window.outerHeight,
      outerWidth: window.outerWidth,
      platform: navigator.platform,
      referrer: document.referrer,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      userAgentData: nav.userAgentData ?? null,
      vendor: navigator.vendor,
      version: '1.0.0',
      connection: nav.connection
        ? {
            downlink: nav.connection.downlink ?? null,
            effectiveType: nav.connection.effectiveType ?? null,
            rtt: nav.connection.rtt ?? null,
            saveData: nav.connection.saveData ?? null,
          }
        : null,
    };
  }
}
